<template>
    <main class="home">
        <section class="home__hero-section">
            <div class="container home__hero-container">
                <div class="home__hero-content">
                    <header class="home__header">
                        <h1 class="home__title">
                            {{ randomReaction }}, en {{ activeType }} koster deg <br>
                            <span class="home__title-num">{{ animatedTotal }}</span> i året
                        </h1>
                    </header>
                    <active-selection class="home__active-selection" />
                    <drop-down
                        :list="selections"
                        class="home__drop-down home__drop-down--mobile"
                        @select="onDropDownChange" />
                </div>
            </div>
        </section>
        <section class="home__comparison-section">
            <div class="container">
                <div class="home__comparison-content">
                    <header class="container home__comparison-header">
                        For {{ animatedTotal }} i året kunne du heller fått alt dette i Vestfold:
                    </header>
                    <div class="container home__comparisons">
                        <comparison-item
                            v-for="item in activeComparisons"
                            :key="item.id"
                            :item="item" />
                    </div>
                    <div class="container home__sign-off">
                        <img src="/money-pile.svg" class="home__money-pile">
                        <p class="larger">
                            <span class="bold">+ {{ animatedRest }}</span><br>
                            til å bruke på akkurat hva du vil
                        </p>
                    </div>
                </div>
            </div>
        </section>
        <section class="home__show-more-section">
            <div class="container home__show-more">
                <router-link :to="detailPath" class="btn">
                    Se hele regnestykket
                </router-link>
            </div>
        </section>
        <section class="home__share-section">
            <div class="container home__share-container">
                <share class="home__share" />
            </div>
        </section>
    </main>
</template>

<script>
import { formatNOK } from '@/utils/currency';
import { kebabCase } from 'lodash';
import DropDown from '@/components/DropDown';
import Share from '@/components/Share';
import ActiveSelection from '@/components/ActiveSelection';
import ComparisonItem from '@/components/ComparisonItem';
export default {
    name: 'Home',
    components: {
        DropDown,
        Share,
        ActiveSelection,
        ComparisonItem
    },
    data () {
        return {
            total: 0,
            rest: 0,
            reactions: ['Wow', 'Jøss', 'Utrolig', 'Oi', 'Gurimalla'],
            randomReaction: ''
        };
    },
    computed: {
        selections () {
            return this.$store.state.selections;
        },
        activeComparisons () {
            return this.$store.getters.activeComparisons || '';
        },
        activeSelection () {
            return this.$store.getters.activeSelection;
        },
        activeLabel () {
            return (this.activeSelection && this.$store.activeSelection.label) || '';
        },
        activeType () {
            return (this.activeSelection && this.activeSelection.type) || '';
        },
        activeRest () {
            const comparePrice = (this.$store.getters.activeSelection && this.$store.getters.activeSelection.total) || 0;
            return comparePrice - ((this.activeComparisons || []).map((v)=>{
                return v.value * v.priceInfo.price * v.priceInfo.unitsPerYear;
            })).reduce((t, v) => (t + v));
        },
        detailPath () {
            return `/${kebabCase(this.activeType)}/detaljer/`;
        },
        activeTotal () {
            return this.activeSelection.total || 0;
        },
        animatedTotal () {
            return formatNOK(this.total.toFixed(0),' kr');
        },
        animatedRest () {
            return formatNOK(this.rest.toFixed(0),' kr');
        }
    },
    watch: {
        activeTotal (val) {
            this.$anime({
                targets: this,
                total: val,
                duration: 1000,
                easing: 'easeOutSine'
            });
        },
        activeRest (val) {
            this.$anime({
                targets: this,
                rest: val,
                duration: 1000,
                easing: 'easeOutSine'
            });
        }
    },
    mounted () {
        const total = this.activeTotal;
        this.$anime({
            targets: this,
            total: total,
            duration: 1000,
            easing: 'easeOutSine'
        });
        const rest = this.activeRest;
        this.$anime({
            targets: this,
            rest: rest,
            duration: 1000,
            easing: 'easeOutSine'
        });
        this.randomReaction = this.reactions[Math.floor(Math.random() * this.reactions.length)];
    },
    methods: {
        onDropDownChange (path) {
            if (this.$route.fullPath === path) {
                return;
            }
            this.$router.push(path);
            this.randomReaction = this.reactions[Math.floor(Math.random() * this.reactions.length)];
        }
    }
};
</script>

<style lang="scss">
.home{
    position: relative;
    display: flex;
    flex-flow: row wrap;
    &__hero-section{
        position: relative;
        width: 100%;
        @include breakpoint(large){
            width: 50%;
            &:before{
                content: '';
                position: absolute;
                top: 40%;
                right: 0;
                width: 128px;
                height: 32px;
                transform: translateX(50%) translateY(-50%);
                background-color: color(light-green);
                z-index: 5;
                box-shadow: 2px 2px 12px 2px rgba(0, 0, 0, 0.05);
            }
            &:after{
                content: '';
                position: absolute;
                top: calc(40% + 40px);
                right: 0;
                width: 128px;
                height: 32px;
                transform: translateX(50%) translateY(-50%);
                background-color: color(light-green);
                z-index: 5;
                box-shadow: 2px 2px 12px 2px rgba(0, 0, 0, 0.05);
            }
        }
    }
    &__hero-container{
        position: relative;
        width: 100%;
    }
    &__hero-content{
        @include breakpoint(large){
            margin-left: auto;
            max-width: calc($content-max-width / 2) + spacing(4);
            padding-right: spacing(2);
        }
    }
    &__header{
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: center;
        width: 100%;
    }
    &__title{
        position: relative;
        z-index: 2;
        font-weight: $WEIGHT_MEDIUM;
        margin-top: 0;
        &-num{
            font-weight: $WEIGHT_BOLD;
            font-size: 140.625%;
        }
        margin-bottom: spacing(-9);
        @include breakpoint(medium only){
            margin-bottom: spacing(-9);
        }
        @include breakpoint(small only){
            margin-bottom: spacing(-8);
        }
    }
    &__drop-down{
        margin-top: spacing(3);
    }
    &__active-selection{
        width: 100%;
        @include breakpoint(large){
            width: 420px;
        }
    }
    &__comparison-section{
        position: relative;
        background-color: color(body-bg);
        width: 100%;
        @include breakpoint(large){
            width: 50%;
            padding-top: $header-height-large + spacing(8);
        }
    }
    &__comparison-content{
        width: 100%;
        @include breakpoint(large){
            margin-right: auto;
            padding-top: spacing(1);
            padding-left: spacing(2);
            max-width: calc($content-max-width / 2) + spacing(4);
        }
    }
    &__comparison-header{
        text-align: center;
        margin-bottom: spacing(7);
        font-weight: $WEIGHT_MEDIUM;
        font-size: 122.2222%;
    }
    &__comparisons{
        display: flex;
        flex-flow: row wrap;
        justify-content: space-between;
    }
    &__money-pile{
        width: 80px;
        height: auto;
        margin-top: spacing(1);
        margin-bottom: spacing(1);
    }
    &__sign-off{
        text-align: center;
        p{
            margin: 0;
            &:first-of-type{
                margin-bottom: 1em;
            }
        }
    }
    &__stamp{
        width: 100%;
        padding: 0;
        &-container{
            text-align: center;
        }
    }
    &__show-more-section,
    &__share-section{
        width: 100%;
    }
    &__show-more{
        display: flex;
        justify-content: center;
    }
    &__share-container{
        display: flex;
        justify-content: center;
    }
}
</style>
